import React, { Fragment, useEffect, useRef, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { Status } from '../../hooks/useFetchState'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Box, Typography } from '@mui/material'
import { WarningAmberOutlined } from '@mui/icons-material'
import { useTypesPriority } from '../../hooks/useTypesPriority'
import { FilterComponent, Filter } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { deleteCourierServiceSchedule, getCouriers } from './config/api'
import { CourierServiceGridDto, tableColumns, createCourierActions } from './config/config'
import {
  CourierCollectionPlannerFormDetail,
  CourierCollectionPlannerFormEdit,
  CourierCollectionPlannerFormEditRef
} from './form'

interface CourierPageProps {}

export const CourierCollectionPlannerPage: React.FC<CourierPageProps> = () => {
  const { state, callApi } = useApi<ApiResponse<CourierServiceGridDto>>()
  const { state: deleteCourierState, callApi: deleteCourierCallApi } =
    useApi<ApiResponse<CourierServiceGridDto>>()

  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const editModal = useModal<{ courier: CourierServiceGridDto }>('Editar courier')
  const deleteModal = useModal<{ courier: CourierServiceGridDto }>('Eliminar courier')
  const viewModal = useModal<{ courier: CourierServiceGridDto }>('Detalle courier')

  const editFormRef = useRef<CourierCollectionPlannerFormEditRef>(null)
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  const pagination = usePagination(5)

  const requestCouriers = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getCouriers(filters, page, pageSize)).catch((err) =>
      openSnackBar(err, false)
    )
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestCouriers(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestDeleteSchedule = () => {
    const courierServiceId = deleteModal.extendedProps?.courier.courierServiceId
    if (!courierServiceId) return

    deleteCourierCallApi(deleteCourierServiceSchedule(courierServiceId))
      .then(() => {
        openSnackBar('Horario eliminado correctamente', true)
        deleteModal.closeModal()
        requestCouriers(selectedFilters, pagination.page, pagination.pageSize)
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }


  const {
    getTypesPriority,
    typesPriorityState: { typesPriority }
  } = useTypesPriority()

  useEffect(() => {
    if (!typesPriority || typesPriority?.length <= 0) {
      getTypesPriority()
    }
    requestCouriers(selectedFilters, 0, pagination.pageSize)
  }, [])

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          options={filterOptions}
          onSearchClick={(filters) => {
            pagination.setPagination(0, pagination.pageSize)
            requestCouriers(filters, 0, pagination.pageSize)
          }}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
        />
        <GenericTableV2<CourierServiceGridDto>
          data={state.data?.items ?? []}
          columns={tableColumns}
          keyField='courierServiceId'
          actions={createCourierActions(
            (courier) => viewModal.openModal('md', { courier }),
            (courier) => editModal.openModal('md', { courier }),
            (courier) => deleteModal.openModal('sm', { courier })
          )}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePaginationChange
          }}
          loading={state.status === Status.Loading}
        />
      </StandardPage>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        onClose={editModal.closeModal}
        title={editModal.title}
        loading={isSavingEdit}
        cancelButton={{
          title: 'Cancelar',
          action: editModal.closeModal,
          disabled: isSavingEdit
        }}
        successButton={{
          title: 'Guardar',
          action: () => editFormRef.current?.save()
        }}
      >
        {editModal.extendedProps && (
          <CourierCollectionPlannerFormEdit
            ref={editFormRef}
            courier={editModal.extendedProps.courier}
            onSavingChange={setIsSavingEdit}
            handleSnackbar={openSnackBar}
            onSave={(success) => {
              if (success) {
                editModal.closeModal()
                requestCouriers(selectedFilters, pagination.page, pagination.pageSize)
              }
            }}
          />
        )}
      </Modal>
      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        onClose={viewModal.closeModal}
        title={viewModal.title}
        cancelButton={{
          title: 'Cerrar',
          action: viewModal.closeModal
        }}
      >
        {viewModal.extendedProps && (
          <CourierCollectionPlannerFormDetail courier={viewModal.extendedProps.courier} />
        )}
      </Modal>
      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        onClose={deleteModal.closeModal}
        title={deleteModal.title}
        loading={deleteCourierState.status === Status.Loading}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar',
          disabled: deleteCourierState.status === Status.Loading
        }}
        successButton={{
          action: requestDeleteSchedule,
          title: 'Eliminar'
        }}
      >
        <Typography variant='body2' component={'h4'}>
          ¿Está seguro que desea eliminar los horarios del courier service{' '}
          <b>{deleteModal.extendedProps?.courier.name}</b>?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 2,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: '#FFF4E5',
            border: '1px solid #FFB300'
          }}
        >
          <WarningAmberOutlined sx={{ fontSize: '1.25rem', color: '#FFB300' }} />
          <Typography variant='caption' color='black'>
            Esta acción no se puede deshacer: se eliminarán todos los horarios semanales y
            excepciones de recolección configurados para este servicio.
          </Typography>
        </Box>
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
